<template>
  <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)">
    <v-card v-if="row" class="kf-rh">
      <v-card-title class="kf-rh__head">
        <div class="kf-rh__id">
          <div class="kf-rh__title">Lender report history · {{ row.reference || row.lead_id }}</div>
          <div class="kf-rh__sub">
            {{ row.product || "-" }} · {{ fmt.mobile(row.mobile) }}
            <template v-if="row.lender_lead_id"> · Lender Lead ID {{ row.lender_lead_id }}</template>
          </div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <LucideIcon name="x" :size="20" />
        </v-btn>
      </v-card-title>

      <v-card-text class="kf-rh__body">
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <div v-else-if="error" class="kf-rh__error">{{ error }}</div>
        <template v-else>
          <dl v-if="lead" class="kf-rh__grid">
            <dt>State</dt>
            <dd>
              {{ stateLabel(lead.state) }}
              <span v-if="lead.offer_id" class="kf-muted">
                · offer {{ lead.offer_id }} ({{ lead.match_method }}, {{ fmt.dateTimeIst(lead.matched_at) }})
              </span>
            </dd>
            <template v-if="lead.unmatched_reason_label">
              <dt>Why unmatched</dt>
              <dd>{{ lead.unmatched_reason_label }}</dd>
            </template>
            <dt>Account</dt>
            <dd>{{ lead.user_id ? "Registered (user " + lead.user_id + ")" : "Not registered" }}</dd>
            <dt>Lender status</dt>
            <dd>{{ lead.status || "-" }}<template v-if="lead.sub_status"> · {{ lead.sub_status }}</template></dd>
            <dt>Disbursal</dt>
            <dd>
              <template v-if="lead.disbursal_amount">
                {{ fmt.currency(lead.disbursal_amount) }} on {{ lead.disbursal_date || "-" }}
              </template>
              <span v-else>-</span>
            </dd>
            <dt>First / last reported</dt>
            <dd>{{ fmt.dateTimeIst(lead.first_seen_at) }} / {{ fmt.dateTimeIst(lead.last_seen_at) }}</dd>
            <template v-if="lead.possible_duplicate_of">
              <dt>Possible duplicate of</dt>
              <dd>{{ lead.possible_duplicate_of }}</dd>
            </template>
          </dl>
          <div v-if="references.length" class="kf-rh__refs">
            Lender-reported as {{ references.join(", ") }}
          </div>

          <div class="kf-rh__label">Report rows ({{ observations.length }})</div>
          <div v-if="!observations.length" class="kf-muted">
            No MIS report row has reached this lead yet.
          </div>
          <table v-else class="kf-rh__table">
            <thead>
              <tr>
                <th>Reported</th>
                <th>Report</th>
                <th>Row</th>
                <th>Status</th>
                <th>Disbursal</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in observations" :key="o.id">
                <td>{{ fmt.dateTimeIst(o.observed_at) }}</td>
                <td>
                  {{ o.report_name || "-" }}
                  <span v-if="o.report_deleted" class="kf-muted">(report deleted)</span>
                  <span v-if="o.source === 'backfill'" class="kf-muted">(recovered)</span>
                </td>
                <td>{{ o.row }}</td>
                <td>{{ o.status || "-" }}<div v-if="o.sub_status" class="kf-muted">{{ o.sub_status }}</div></td>
                <td>
                  <template v-if="o.disbursal_amount">
                    {{ fmt.currency(o.disbursal_amount) }}
                    <div class="kf-muted">{{ o.disbursal_date || "" }}</div>
                  </template>
                  <span v-else>-</span>
                </td>
                <td>{{ outcomeLabel(o) }}</td>
              </tr>
            </tbody>
          </table>

          <template v-if="events.length">
            <div class="kf-rh__label">Links and matches</div>
            <ul class="kf-rh__events">
              <li v-for="(e, i) in events" :key="i">
                {{ fmt.dateTimeIst(e.at) }} · {{ eventLabel(e) }} · {{ e.actor }}
                <span v-if="e.detail && e.detail.note" class="kf-muted">— “{{ e.detail.note }}”</span>
              </li>
            </ul>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import fmt from "@/app-js/karrofin/format";
import api from "@/app-js/karrofin/api";

const STATES = { unmatched: "Unmatched", ambiguous: "Several possible offers", matched: "Matched" };
const OUTCOMES = {
  applied: "Written to the offer",
  skipped_older: "Older than the offer's status, not written",
  unmatched: "Reached no offer",
  tracked_click: "Kept on the tracked click",
};
const EVENTS = {
  created: "First reported",
  linked_user: "Linked to the account",
  auto_matched: "Matched automatically",
  manual_matched: "Matched by ops",
  marked_duplicate: "Flagged as a possible duplicate",
};

/**
 * Every MIS row a lender sent about one Leads row, and every link or match made
 * to it. Loads on open; the row it is handed decides which history is asked for
 * (api.getReportHistory).
 */
export default {
  name: "KfReportHistoryDialog",
  props: {
    value: { type: Boolean, default: false },
    row: { type: Object, default: null },
  },
  data() {
    return { fmt, loading: false, error: "", lead: null, observations: [], events: [], references: [] };
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
  },
  methods: {
    load() {
      if (!this.row) return;
      this.loading = true;
      this.error = "";
      this.lead = null;
      this.observations = [];
      this.events = [];
      this.references = [];
      api
        .getReportHistory(this.row)
        .then((result) => {
          this.lead = result.lead || null;
          this.observations = result.observations || [];
          this.events = result.events || [];
          this.references = result.references || [];
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.error = (data && data.detail) || "Could not load the report history";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    stateLabel(state) {
      return STATES[state] || state || "-";
    },
    outcomeLabel(o) {
      const label = OUTCOMES[o.outcome] || o.outcome;
      return o.offer_id && o.outcome === "applied" ? `${label} (offer ${o.offer_id})` : label;
    },
    eventLabel(e) {
      const label = EVENTS[e.action] || e.action;
      return e.offer_id ? `${label} → offer ${e.offer_id}` : label;
    },
  },
};
</script>

<style scoped>
.kf-rh__head {
  align-items: flex-start;
  flex-wrap: nowrap;
}
.kf-rh__id {
  min-width: 0;
}
.kf-rh__title {
  font-size: var(--kf-fs-body);
  font-weight: 700;
  color: #000;
}
.kf-rh__sub,
.kf-muted {
  font-size: var(--kf-fs-label);
  color: #54636f;
  font-weight: 400;
}
.kf-rh__grid {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 6px 12px;
  margin: 0 0 14px;
  font-size: var(--kf-fs-hint);
  color: #000;
}
.kf-rh__grid dt {
  color: #54636f;
  font-weight: 600;
}
.kf-rh__grid dd {
  margin: 0;
  min-width: 0;
}
.kf-rh__refs {
  font-size: var(--kf-fs-hint);
  margin-bottom: 10px;
}
.kf-rh__label {
  font-size: var(--kf-fs-label);
  color: #54636f;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 12px 0 6px;
}
.kf-rh__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--kf-fs-hint);
  color: #000;
}
.kf-rh__table th {
  text-align: left;
  color: #54636f;
  font-weight: 600;
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid var(--kf-border);
}
.kf-rh__table td {
  padding: 6px 8px 6px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--kf-border);
}
.kf-rh__events {
  padding-left: 18px;
  font-size: var(--kf-fs-hint);
}
.kf-rh__error {
  color: #b42318;
  font-size: var(--kf-fs-hint);
}
@media (max-width: 600px) {
  .kf-rh__grid {
    grid-template-columns: 1fr;
  }
}
</style>
